import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Input from "../../../components/common/Input";
import Button from "../../../components/common/Button";
import { resetPasswordThunk } from "../../../store/thunks/adminThunks";
import { CheckIcon, ExclamationCircleIcon } from "@heroicons/react/solid";

const ChangePasswordStep = ({ onComplete }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const passwordRequirements = [
    { regex: /.{8,}/, text: "At least 8 characters" },
    { regex: /[A-Z]/, text: "One uppercase letter" },
    { regex: /[a-z]/, text: "One lowercase letter" },
    { regex: /[0-9]/, text: "One number" },
    { regex: /[!@#$%^&*]/, text: "One special character" },
  ];

  const checkPasswordStrength = (password) => {
    return passwordRequirements.map((req) => ({
      ...req,
      met: req.regex.test(password),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    if (!formData.newPassword) {
      newErrors.newPassword = "New password is required";
    } else {
      const unmetRequirements = passwordRequirements.filter(
        (req) => !req.regex.test(formData.newPassword)
      );
      if (unmetRequirements.length > 0) {
        newErrors.newPassword = "Password does not meet all requirements";
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      await dispatch(
        resetPasswordThunk({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
        })
      ).unwrap();

      onComplete({
        passwordChanged: true,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      setErrors({
        general: error || "Failed to change password",
      });
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = checkPasswordStrength(formData.newPassword);

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Secure Your Account</h3>
        <p className="text-gray-600">
          For security reasons, you must change the default password before
          continuing.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {errors.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {errors.general}
          </div>
        )}

        <Input
          label="Current Password"
          type="password"
          name="currentPassword"
          value={formData.currentPassword}
          onChange={handleChange}
          error={errors.currentPassword}
          placeholder="Enter your current password"
          required
        />

        <div>
          <Input
            label="New Password"
            type="password"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            error={errors.newPassword}
            placeholder="Enter a strong password"
            required
          />

          {formData.newPassword && (
            <div className="mt-3 space-y-1">
              <p className="text-sm font-medium text-gray-700">
                Password Requirements:
              </p>
              {passwordStrength.map((req, index) => (
                <div
                  key={index}
                  className={`flex items-center text-sm ${
                    req.met ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  {req.met ? (
                    <CheckIcon className="w-4 h-4 mr-2" />
                  ) : (
                    <ExclamationCircleIcon className="w-4 h-4 mr-2" />
                  )}
                  {req.text}
                </div>
              ))}
            </div>
          )}
        </div>

        <Input
          label="Confirm New Password"
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
          placeholder="Re-enter your new password"
          required
        />

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            disabled={loading}
          >
            Change Password & Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ChangePasswordStep;
